import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Spin, Button, Empty } from 'antd';
import { ArrowLeftOutlined, PrinterOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { Trans, useTranslation } from 'react-i18next';
import axiosInstance from '../../variables/api/axiosInstance';
import logoCarteBrune from '../../assets/images/logo.png';
import { IndicesReedition, markPrinted, wasPrinted } from './indiceReedition';
import { useReglagesIndices } from './useReglagesIndices';
import './ficheDeclaration.css';
import BlocSignature from '../../components/BlocSignature';
import { useAvertissementSignature } from '../../components/avertissementSignature';
import CaseApposerSignature from '../../components/CaseApposerSignature';

/* ─────────────────────────────────────────────────────────────
   Fiche de Déclaration Sinistre - impression A4 portrait, 1 page.
   Calque du formulaire papier BNCB (bilingue FR / EN).
   Route : /sinistres/:id/fiche
   ───────────────────────────────────────────────────────────── */
export default function FicheDeclaration() {

  const { t } = useTranslation('sinistres');

  /* Les signatures sont temporaires : celle qui valait a l'ouverture de cet
     ecran peut avoir expire. L'etat est relu au clic, et l'agent est averti que
     la feuille sortira vierge - puis libre a lui d'imprimer. */
  const { confirmerPuisImprimer } = useAvertissementSignature('FICHE_DECLARATION');
  /* Le paramétrage dit qu'un document est signé ; il ne peut pas savoir que CE
     tirage-là n'en veut pas. Le responsable garde le dernier mot. */
  const [apposer, setApposer] = useState(true);
  const { id } = useParams();
  const reglagesIndices = useReglagesIndices();
  const navigate = useNavigate();
  const [sinistre, setSinistre] = useState(null);
  const [victimes, setVictimes] = useState([]);
  const [assure, setAssure] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sRes, vRes] = await Promise.all([
        axiosInstance.get(`/v1/sinistres/${id}`),
        axiosInstance.get(`/v1/victimes/sinistre/${id}`),
      ]);
      const s = sRes.data?.data;
      setSinistre(s);
      setVictimes(vRes.data?.data || []);
      if (s?.assureTrackingId) {
        try {
          const aRes = await axiosInstance.get(`/v1/assures/${s.assureTrackingId}`);
          setAssure(aRes.data?.data);
        } catch { /* ignore */ }
      }
    } catch {
      setSinistre(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div className="fiche-page"><Spin /></div>;
  }
  if (!sinistre) {
    return (
      <div className="fiche-page">
        <Empty description={t('fiche.introuvable')} />
        <Button onClick={() => navigate('/sinistres')} icon={<ArrowLeftOutlined />} style={{ marginTop: 12 }}>
          {t('fiche.retour')}
        </Button>
      </div>
    );
  }

  const adversaires = victimes.filter(v => v.estAdversaire);
  const firstAdv = adversaires[0] || {};

  /* La remorque est un engin à part entière : plaque, marque, modèle et - très
     souvent - contrat d'assurance qui lui sont propres. La déclaration la saisit
     des deux côtés, la fiche imprimée n'en montrait rien.

     Le bloc est CONDITIONNEL, et ce n'est pas un détail de style : la fiche tient
     sur une page A4 et c'est ce qui en fait un document utilisable au guichet.
     Quatre lignes vides sur chaque dossier sans remorque - l'écrasante majorité -
     la feraient déborder sur une seconde page pour ne rien dire. */
  const remorqueAssure = assure?.aRemorque
    || assure?.remorqueImmatriculation || assure?.remorqueMarque
    || assure?.remorqueModele || assure?.remorqueAssureur;
  const remorqueAdverse = firstAdv.aRemorque
    || firstAdv.remorqueImmatriculation || firstAdv.remorqueMarque
    || firstAdv.remorqueModele || firstAdv.remorqueAssureur;
  const afficherRemorques = !!(remorqueAssure || remorqueAdverse);

  const fmtDate = (d, fallback = '-') => d ? dayjs(d).format('DD/MM/YYYY') : fallback;
  const fmtTime = (t) => {
    if (!t) return '-';
    const [hh, mm] = String(t).split(':');
    return `${hh}:${mm || '00'}`;
  };
  const or = (v, fallback = '-') => (v === null || v === undefined || v === '') ? fallback : v;
  // Nettoie les clés paramétriques stockées sous forme "marque.citroen" → "Citroen"
  const fmtMarque = (v) => {
    if (!v) return '-';
    if (!v.includes('.')) return v;
    const raw = v.split('.').pop().replace(/_/g, ' ');
    return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
  };
  // Libellé bilingue du formulaire papier : la langue de l'interface, puis sa glose en italique.
  const L = (cle, em = <em />) => <Trans t={t} i18nKey={`fiche.libelles.${cle}`} components={{ em }} />;
  const checkbox = (bool) => <span className="fiche-check">{bool ? '☑' : '☐'}</span>;

  const dommageChecks = (target) => ({
    corporel: target === 'CORPOREL' || target === 'MIXTE',
    materiel: target === 'MATERIEL' || target === 'MIXTE',
  });
  const dDeclarant = dommageChecks(sinistre.typeDommage);
  const dAdverse = dommageChecks(firstAdv?.typeDommage);

  const conducteur = sinistre.conducteurEstAssure ? {
    nom: assure?.nomComplet || sinistre.assureNomComplet,
    dateNaissance: null,
    numeroPermis: sinistre.conducteurNumeroPermis,
    categoriesPermis: sinistre.conducteurCategoriesPermis,
    dateDelivrance: sinistre.conducteurDateDelivrance,
    lieuDelivrance: sinistre.conducteurLieuDelivrance,
  } : {
    nom: [sinistre.conducteurNom, sinistre.conducteurPrenom].filter(Boolean).join(' '),
    dateNaissance: sinistre.conducteurDateNaissance,
    numeroPermis: sinistre.conducteurNumeroPermis,
    categoriesPermis: sinistre.conducteurCategoriesPermis,
    dateDelivrance: sinistre.conducteurDateDelivrance,
    lieuDelivrance: sinistre.conducteurLieuDelivrance,
  };

  const dejaImprime = wasPrinted('fiche', id);

  return (
    <div className="fiche-page">
      <div className="fiche-toolbar no-print">
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(`/sinistres/${id}`)}>
          {t('fiche.retourDossier')}
        </Button>
        <CaseApposerSignature codeDocument="FICHE_DECLARATION" valeur={apposer} onChange={setApposer} />
        <Button type="primary" icon={<PrinterOutlined />} onClick={() => confirmerPuisImprimer(() => { markPrinted('fiche', id); window.print(); })} style={{ background: '#1a6b3c', borderColor: '#1a6b3c' }}>
          {t('fiche.imprimer')}
        </Button>
      </div>

      <div className="fiche-sheet">
        {/* ═══════ Entête centré (logo + titre + rédacteur) ═══════ */}
        <div className="fiche-header">
          <img src={logoCarteBrune} alt={t('fiche.logoAlt')} className="fiche-logo-img" />
          <div className="fiche-header-center">
            <h1 className="fiche-title">{t('fiche.titre')}</h1>
            <div className="fiche-subtitle">{t('fiche.sousTitre')}</div>
            <div className="fiche-redacteur">
              <em>{t('fiche.redacteur')}</em> {or(sinistre.redacteurNomPrenom)}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <IndicesReedition
              dejaModifie={!!sinistre?.dejaModifie}
              dejaImprime={dejaImprime}
              reglages={reglagesIndices}
            />
          </div>
        </div>

        {/* ═══════ Bandeau BUREAU NATIONAL TOGO (ligne pleine largeur) ═══════ */}
        <div className="fiche-top-banner">
          <span className="label">{L('bureauNational')}</span>
          <span className="value">{(sinistre.paysGestionnaireLibelle || 'TOGO').toUpperCase()}</span>
        </div>

        {/* ═══════ Identification - 2 grandes cellules sans lignes internes ═══════ */}
        <div className="fiche-id-block">
          <div className="fiche-id-col">
            <div className="fiche-id-field">
              <div className="fiche-label">{L('carteBrune')}</div>
              <div className="fiche-value mono">{or(sinistre.numeroAttestation || assure?.numeroAttestation)}</div>
            </div>
            <div className="fiche-id-field">
              <div className="fiche-label">{L('validite')}</div>
              <div className="fiche-value">{fmtDate(sinistre.dateEcheance)}</div>
            </div>
            <div className="fiche-id-field">
              <div className="fiche-label">{L('police')}</div>
              <div className="fiche-value mono">{or(assure?.numeroPolice || sinistre.numeroPoliceAssureur)}</div>
            </div>
            <div className="fiche-id-field">
              <div className="fiche-label">{L('assureur')}</div>
              <div className="fiche-value">{or(sinistre.organismeMembreRaisonSociale)}</div>
            </div>
            <div className="fiche-id-field">
              <div className="fiche-label">
                {L('assureursSecondaires')}
              </div>
              <div className="fiche-value">
                {(sinistre.assureursSecondaires || []).length === 0
                  ? '-'
                  : sinistre.assureursSecondaires
                    .map(a => a.raisonSociale || a.code)
                    .filter(Boolean)
                    .join(', ')}
              </div>
            </div>
            <div className="fiche-id-field">
              <div className="fiche-label">{L('assure')}</div>
              <div className="fiche-value">{or(assure?.nomComplet || sinistre.assureNomComplet)}</div>
            </div>
            <div className="fiche-id-field">
              <div className="fiche-label">{L('profession')}</div>
              <div className="fiche-value">{or(assure?.profession)}</div>
            </div>
            <div className="fiche-id-field">
              <div className="fiche-label">{L('adresse')}</div>
              <div className="fiche-value">{or(assure?.adresse)}</div>
            </div>
          </div>
          <div className="fiche-id-col">
            <div className="fiche-id-field">
              <div className="fiche-label">{L('numeroSinistre')}</div>
              <div className="fiche-value mono">{or(sinistre.numeroSinistreLocal)}</div>
            </div>
            {/* Colonne la plus courte du bloc (4 champs contre 8) : les deux références
                n'allongent pas la page. */}
            <div className="fiche-id-field">
              <div className="fiche-label">{L('numeroSinistreAssureur')}</div>
              <div className="fiche-value mono">{or(sinistre.numeroSinistreAssureur)}</div>
            </div>
            <div className="fiche-id-field">
              <div className="fiche-label">{L('numeroSinistreHomologue')}</div>
              <div className="fiche-value mono">{or(sinistre.numeroSinistreHomologue)}</div>
            </div>
            <div className="fiche-id-field">
              <div className="fiche-label">{L('dateAccident')}</div>
              <div className="fiche-value">{fmtDate(sinistre.dateAccident)}</div>
            </div>
            <div className="fiche-id-field">
              <div className="fiche-label">{L('heureAccident')}</div>
              <div className="fiche-value">{fmtTime(sinistre.heureAccident)}</div>
            </div>
            <div className="fiche-id-field">
              <div className="fiche-label">{L('lieu')}</div>
              <div className="fiche-value">{or(sinistre.lieuAccident || sinistre.ville)}</div>
            </div>
          </div>
        </div>

        {/* ═══════ Renseignements véhicules ═══════ */}
        <table className="fiche-table">
          <colgroup><col style={{ width: '50%' }} /><col style={{ width: '50%' }} /></colgroup>
          <tbody>
            <tr>
              <td colSpan={2} className="section-band">
                {L('vehicules')}
              </td>
            </tr>
            <tr>
              <td className="section-band">{L('vehiculeDeclarant')}</td>
              <td className="section-band">{L('vehiculeAdverse')}</td>
            </tr>
            <tr>
              <td>
                <div className="fiche-label">{L('marque')}</div>
                <div className="fiche-value">{fmtMarque(assure?.marqueVehicule)}</div>
              </td>
              <td>
                <div className="fiche-label">{L('marque')}</div>
                <div className="fiche-value">{fmtMarque(firstAdv.marqueVehicule)}</div>
              </td>
            </tr>
            <tr>
              <td>
                <div className="fiche-label">{L('immatriculation')}</div>
                <div className="fiche-value mono">{or(assure?.immatriculation)}</div>
              </td>
              <td>
                <div className="fiche-label">{L('immatriculationAdverse')}</div>
                <div className="fiche-value mono">{or(firstAdv.immatriculation)}</div>
              </td>
            </tr>
            <tr>
              <td>
                <div className="fiche-label">{L('prochaineVisite')}</div>
                <div className="fiche-value">{fmtDate(assure?.prochaineVT)}</div>
              </td>
              <td>
                <div className="fiche-label">{L('prochaineVisite')}</div>
                <div className="fiche-value">{fmtDate(firstAdv.prochaineVT)}</div>
              </td>
            </tr>
            <tr>
              <td>
                <div className="fiche-label">{L('personnesTransportees')}</div>
                <div className="fiche-value">{or(assure?.nbPersonnesABord)}</div>
              </td>
              <td>
                <div className="fiche-label">{L('personnesTransportees')}</div>
                <div className="fiche-value">{or(firstAdv.nbPersonnesABord)}</div>
              </td>
            </tr>
            <tr>
              <td>
                <div className="fiche-label">{L('assureurPrincipal')}</div>
                <div className="fiche-value">{or(sinistre.organismeMembreRaisonSociale)}</div>
              </td>
              <td>
                <div className="fiche-label">{L('assuranceAdverse')}</div>
                <div className="fiche-value">{or(firstAdv.assureurAdverse)}</div>
              </td>
            </tr>

            {afficherRemorques && (
              <>
                <tr>
                  <td className="section-band">{L('remorque')}</td>
                  <td className="section-band">{L('remorqueAdverse')}</td>
                </tr>
                <tr>
                  <td>
                    <div className="fiche-label">{L('immatriculationRemorque')}</div>
                    <div className="fiche-value mono">
                      {remorqueAssure ? or(assure?.remorqueImmatriculation) : t('fiche.sansRemorque')}
                    </div>
                  </td>
                  <td>
                    <div className="fiche-label">{L('immatriculationRemorque')}</div>
                    <div className="fiche-value mono">
                      {remorqueAdverse ? or(firstAdv.remorqueImmatriculation) : t('fiche.sansRemorque')}
                    </div>
                  </td>
                </tr>
                <tr>
                  <td>
                    <div className="fiche-label">{L('marqueModele')}</div>
                    <div className="fiche-value">
                      {remorqueAssure
                        ? or([fmtMarque(assure?.remorqueMarque), assure?.remorqueModele]
                          .filter((x) => x && x !== '-').join(' · '))
                        : '-'}
                    </div>
                  </td>
                  <td>
                    <div className="fiche-label">{L('marqueModele')}</div>
                    <div className="fiche-value">
                      {remorqueAdverse
                        ? or([fmtMarque(firstAdv.remorqueMarque), firstAdv.remorqueModele]
                          .filter((x) => x && x !== '-').join(' · '))
                        : '-'}
                    </div>
                  </td>
                </tr>
                <tr>
                  <td>
                    {/* Contrat souvent distinct de celui du tracteur : c'est
                        précisément l'information qu'on vient chercher ici. */}
                    <div className="fiche-label">{L('assureurRemorque')}</div>
                    <div className="fiche-value">
                      {remorqueAssure ? or(assure?.remorqueAssureur) : '-'}
                    </div>
                  </td>
                  <td>
                    <div className="fiche-label">{L('assureurRemorque')}</div>
                    <div className="fiche-value">
                      {remorqueAdverse ? or(firstAdv.remorqueAssureur) : '-'}
                    </div>
                  </td>
                </tr>
              </>
            )}
          </tbody>
        </table>

        {/* ═══════ Conducteurs ═══════ */}
        <table className="fiche-table">
          <colgroup><col style={{ width: '50%' }} /><col style={{ width: '50%' }} /></colgroup>
          <tbody>
            <tr>
              <td className="section-band">{L('conducteur')}</td>
              <td className="section-band">{L('conducteurAdverse')}</td>
            </tr>
            <tr>
              <td>
                <div className="fiche-label">{L('nom')}</div>
                <div className="fiche-value">{or(conducteur.nom)}</div>
              </td>
              <td>
                <div className="fiche-label">{L('nom')}</div>
                <div className="fiche-value">{or([firstAdv.nom, firstAdv.prenoms].filter(Boolean).join(' '))}</div>
              </td>
            </tr>
            <tr>
              <td>
                <div className="fiche-label">{L('dateNaissance')}</div>
                <div className="fiche-value">{fmtDate(conducteur.dateNaissance)}</div>
              </td>
              <td>
                <div className="fiche-label">{L('dateNaissance')}</div>
                <div className="fiche-value">{fmtDate(firstAdv.dateNaissance)}</div>
              </td>
            </tr>
            <tr>
              <td>
                <div className="fiche-label">{L('numeroPermis')}</div>
                <div className="fiche-value mono">{or(conducteur.numeroPermis)}</div>
              </td>
              <td>
                <div className="fiche-label">{L('numeroPermis')}</div>
                <div className="fiche-value mono">{or(firstAdv.numeroPermis)}</div>
              </td>
            </tr>
            <tr>
              <td>
                <div className="fiche-label">{L('categorie')}</div>
                <div className="fiche-value">{or(conducteur.categoriesPermis)}</div>
              </td>
              <td>
                <div className="fiche-label">{L('categorie')}</div>
                <div className="fiche-value">{or(firstAdv.categoriesPermis)}</div>
              </td>
            </tr>
            <tr>
              <td>
                <div className="fiche-label">{L('dateLieuDelivrance')}</div>
                <div className="fiche-value">
                  {[fmtDate(conducteur.dateDelivrance, ''), conducteur.lieuDelivrance].filter(Boolean).join(t('fiche.separateurLieu')) || '-'}
                </div>
              </td>
              <td>
                <div className="fiche-label">{L('dateLieuDelivrance')}</div>
                <div className="fiche-value">
                  {[fmtDate(firstAdv.dateDelivrance, ''), firstAdv.lieuDelivrance].filter(Boolean).join(t('fiche.separateurLieu')) || '-'}
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        {/* ═══════ Dommages ═══════ */}
        <table className="fiche-table">
          <colgroup><col style={{ width: '50%' }} /><col style={{ width: '50%' }} /></colgroup>
          <tbody>
            <tr>
              <td colSpan={2} className="section-band">
                {L('dommages')}
              </td>
            </tr>
            <tr>
              <td className="section-band">{L('dommagesBande')}</td>
              <td className="section-band">{L('dommagesBande')}</td>
            </tr>
            <tr>
              <td>
                <div className="fiche-dommages-row">
                  <span className="fiche-value">
                    {L('corporels', <em style={{ color: '#5a6676', fontStyle: 'italic', fontSize: '6.8pt' }} />)}
                    &nbsp;&nbsp;{checkbox(dDeclarant.corporel)}
                  </span>
                  <span className="fiche-value">
                    {L('materiels', <em style={{ color: '#5a6676', fontStyle: 'italic', fontSize: '6.8pt' }} />)}
                    &nbsp;&nbsp;{checkbox(dDeclarant.materiel)}
                  </span>
                </div>
              </td>
              <td>
                <div className="fiche-dommages-row">
                  <span className="fiche-value">
                    {L('corporels', <em style={{ color: '#5a6676', fontStyle: 'italic', fontSize: '6.8pt' }} />)}
                    &nbsp;&nbsp;{checkbox(dAdverse.corporel)}
                  </span>
                  <span className="fiche-value">
                    {L('materiels', <em style={{ color: '#5a6676', fontStyle: 'italic', fontSize: '6.8pt' }} />)}
                    &nbsp;&nbsp;{checkbox(dAdverse.materiel)}
                  </span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        {/* ═══════ Bloc unifié (trajet + rapport + circonstances + déclarant + signature) ═══════ */}
        <div className="fiche-bloc-unifie">
          {/* Ligne 1 : trajet */}
          <div className="fiche-bloc-row">
            <div className="fiche-bloc-cell">
              <span className="fiche-label">{L('venaitDe')}</span>
              &nbsp; <strong style={{ fontSize: '8.1pt' }}>{or(sinistre.provenance)}</strong>
            </div>
            <div className="fiche-bloc-cell">
              <span className="fiche-label">{L('dirigeaitVers')}</span>
              &nbsp; <strong style={{ fontSize: '8.1pt' }}>{or(sinistre.destination)}</strong>
            </div>
          </div>

          {/* Ligne 2 : rapport de police */}
          <div className="fiche-bloc-row">
            <div className="fiche-bloc-cell">
              <span className="fiche-label">{L('rapportPolice')}</span>
              &nbsp;&nbsp;{checkbox(sinistre.pvEtabli)} {t('fiche.oui')}
              &nbsp;&nbsp;{checkbox(!sinistre.pvEtabli)} {t('fiche.non')}
            </div>
            <div className="fiche-bloc-cell">
              <span className="fiche-label">{L('quelleLocalite')}</span>
              &nbsp; <strong style={{ fontSize: '8.1pt' }}>{or(sinistre.entiteConstatNom)}</strong>
            </div>
          </div>

          {/* Titre circonstances */}
          <div className="fiche-bloc-titre">
            {L('circonstances')}
          </div>

          {/* Texte circonstances */}
          <div className="fiche-circonstances">
            {or(sinistre.circonstances, '')}
          </div>

          {/* Titre déclarant */}
          <div className="fiche-bloc-titre">
            {L('declarant')}
          </div>

          {/* Nom déclarant + téléphone */}
          <div className="fiche-declarant-line">
            <div className="dl-nom">
              <span className="dl-label">{t('fiche.nomName')}</span>
              <strong>{or([sinistre.declarantNom, sinistre.declarantPrenom].filter(Boolean).join(' '))}</strong>
            </div>
            <div className="dl-tel">
              <span className="dl-label">{t('fiche.telephone')}</span>
              {or(sinistre.declarantTelephone)}
            </div>
          </div>
        </div>

        {/* ═══════ HORS rectangle - date à gauche, signature à droite ═══════

            La date et la signature étaient l'une SOUS l'autre, et occupaient deux
            fois la hauteur pour deux informations qui se lisent d'un même regard :
            « fait à telle date, par telle personne ». Côte à côte, elles tiennent
            dans la place que la signature réservait déjà à elle seule - ce qui
            compte sur un document qui doit rester sur une page A4.

            L'espace de signature garde sa hauteur : il sert encore de place à
            signer et à cacheter à la main quand aucune signature n'est apposée. */}
        <div className="fiche-signature-zone">
          <div className="fiche-signature-rangee">
            <div className="fiche-fait-a-colonne">
              <Trans t={t} i18nKey="fiche.faitA" values={{ date: fmtDate(sinistre.dateDeclaration) }} components={{ strong: <strong /> }} />
              <br /><em>{t('fiche.faitAGlose')}</em>
            </div>

            <div className="fiche-signature-colonne">
              <div className="fiche-pour-bncb">{t('fiche.pourBureauNational')}</div>
              <div className="fiche-se-label">
                {L('secretaireExecutif')}
              </div>
              {/* Dans les 30 mm déjà réservés : la signature prend la place du vide,
                  elle n'ajoute pas de hauteur. Le nom et la fonction s'impriment
                  dessous - un trait sans auteur ne prouve rien au lecteur. */}
              <div className="fiche-signature-espace">
                <BlocSignature
                  codeDocument="FICHE_DECLARATION"
                  actif={apposer}
                  sinistreTrackingId={sinistre?.sinistreTrackingId}
                  hauteurMm={20}
                  avecIdentite
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
